import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { postPayment } from '../../../services/Api';
import { getVbankExpireAfterDays } from '../VBankDate';
import { decodeToken, handleFocus } from '../../../utils';

const ticketProducts = [
    { id: 'ticket-10', group: '횟수 차감 상품', name: '이력서 일반 열람권 10개', price: '10,000원', amount: 10000, code: 'RS_COUNT_10' },
    { id: 'ticket-20', group: '횟수 차감 상품', name: '이력서 일반 열람권 20개', price: '18,000원', amount: 18000, code: 'RS_COUNT_20' },
    { id: 'ticket-50', group: '횟수 차감 상품', name: '이력서 일반 열람권 50개', price: '40,000원', amount: 40000, code: 'RS_COUNT_50' },
    { id: 'ticket-14', group: '기간 상품', name: '이력서 자유 열람권 14일', price: '150,000원', amount: 150000, code: 'RS_PERIOD_14' },
    { id: 'ticket-28', group: '기간 상품', name: '이력서 자유 열람권 28일', price: '280,000원', amount: 280000, code: 'RS_PERIOD_28' },
];

const ticketPurchaseTableStyles = {
    table: {
        tableLayout: 'fixed',
        width: '100%',
    },
    colGroup: {
        width: '25%',
    },
    colCheck: {
        width: '8%',
    },
    colName: {
        width: '43%',
    },
    colPrice: {
        width: '24%',
    },
    cell: {
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'anywhere',
    },
    headerCell: {
        display: 'table-cell',
    },
    groupCell: {
        display: 'table-cell',
        textAlign: 'center',
        verticalAlign: 'middle',
        fontWeight: 600,
        background: '#f4f6f8',
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'anywhere',
    },
    checkCell: {
        width: 'auto',
        paddingRight: '4px',
        paddingLeft: '4px',
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'anywhere',
    },
    priceCell: {
        width: 'auto',
        whiteSpace: 'nowrap',
        overflowWrap: 'normal',
    },
};

const TicketPurchase = ({ onClose, defaultProductId = 'ticket-50' }) => {
    const [selectedProduct, setSelectedProduct] = useState(defaultProductId);
    const [paymentMethod, setPaymentMethod] = useState('Card');
    const [payloadReceipt, setPayloadReceipt] = useState({
        receiptType: 'company',
        nameKor: '',
        mobileNo: '',
        email: '',
        receiptNum: '',
    });

    const token = useSelector(({ auth }) => auth.login.token);
    const [mSeq, setMSeq] = useState('');
    const [memberId, setMemberId] = useState('');

    // inicis 결제 라이브러리 로드
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://stgstdpay.inicis.com/stdjs/INIStdPay.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        const decoded = decodeToken(token);
        if (decoded) {
            setMSeq(decoded.seq);
            setMemberId(decoded.memberID);
        }
    }, [token]);

    const handleChangeReceipt = (e) => {
        const { value, name } = e.target;
        const regex = /^[0-9]*$/;
        if (name === 'receiptNum' && !regex.test(value)) {
            return;
        }

        setPayloadReceipt((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateReceiptCompany = (insertNum) => {
        if (!insertNum) return;
        const regsplitNum = insertNum
            .replace(/-/gi, '')
            .split('')
            .map(function (item) {
                return parseInt(item, 10);
            });

        if (regsplitNum.length === 10) {
            const regkey = [1, 3, 7, 1, 3, 7, 1, 3, 5];
            let regNumSum = 0;
            for (var i = 0; i < regkey.length; i++) {
                regNumSum += regkey[i] * regsplitNum[i];
            }
            regNumSum += parseInt((regkey[8] * regsplitNum[8]) / 10, 10);
            const regCheck = Math.floor(regsplitNum[9]) === (10 - (regNumSum % 10)) % 10;

            return regCheck;
        }
        return false;
    };

    const validatePaymentDetails = () => {
        if (!payloadReceipt.nameKor || !payloadReceipt.mobileNo || !payloadReceipt.email) {
            alert('결제자 정보 입력해 주세요.');
            if (!payloadReceipt.nameKor) {
                handleFocus('nameKor');
            } else if (!payloadReceipt.mobileNo) {
                handleFocus('mobileNo');
            }
            return false;
        }

        if (!/^[0-9]{2,3}-[0-9]{3,4}-[0-9]{4}$/.test(payloadReceipt.mobileNo)) {
            alert('입력하신 휴대폰 번호가 올바른 형식이 아닙니다.\n다시 확인해 주세요. (예: 010-1234-5678)');
            handleFocus('mobileNo');
            return false;
        }

        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(payloadReceipt.email)) {
            alert('유효하지 않은 이메일 주소입니다.');
            handleFocus('email');
            return false;
        }

        if (payloadReceipt.nameKor.length >= 50) {
            alert('이름 길이가 적합하지 않습니다.');
            handleFocus('nameKor');
            return false;
        }

        if (
            (paymentMethod === 'DirectBank' || paymentMethod === 'VBank') &&
            (payloadReceipt.receiptNum === undefined || payloadReceipt.receiptNum === '')
        ) {
            alert('현금영수증을 신청할 번호를 입력해 주세요.');
            handleFocus('receiptNum');
            return false;
        }

        if (
            (paymentMethod === 'DirectBank' || paymentMethod === 'VBank') &&
            payloadReceipt.receiptType === 'company' &&
            !validateReceiptCompany(payloadReceipt?.receiptNum)
        ) {
            alert('현금영수증을 신청번호(사업자번호)가 올바르지 않습니다.');
            handleFocus('receiptNum');
            return false;
        } else if (
            (paymentMethod === 'DirectBank' || paymentMethod === 'VBank') &&
            payloadReceipt.receiptType === 'person' &&
            !/^010[0-9]{8}$/.test(payloadReceipt?.receiptNum)
        ) {
            alert(`현금영수증 신청번호(사업자번호)가 올바르지 않습니다.
- 없이 입력해 주세요. (예: 01012345678)
`);
            handleFocus('receiptNum');
            return false;
        }

        return true;
    };

    const detectDevice = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const platform = navigator.platform.toLowerCase();

        if (/android/.test(userAgent)) {
            return 'android';
        } else if (/iphone|ipad|ipod/.test(userAgent)) {
            return 'ios';
        } else if (/mac/.test(platform)) {
            return 'mac';
        } else if (/win/.test(platform)) {
            return 'windows';
        } else {
            return 'Unknown';
        }
    };

    const handleSubmit = () => {
        const product = ticketProducts.find((item) => item.id === selectedProduct);
        if (!product) {
            window.alert('상품을 선택해 주세요.');
            return;
        }

        if (!validatePaymentDetails()) {
            return;
        }

        const totalPay = product.amount;
        const goodName = product.name;
        const productCode = product.code;

        const deviceType = detectDevice();
        const isMobile = deviceType === 'android' || deviceType === 'ios';
        const device = isMobile ? 'mob' : 'pc';

        const vbankExpire = getVbankExpireAfterDays(3);
        const acceptMethodValue = `HPP(1):below1000:centerCd(Y):vbank(${vbankExpire})`;

        const payFrom = document.querySelector('#payFrom');
        if (payFrom) {
            payFrom.remove();
        }

        postPayment({ totalPay })
            .then(({ data }) => {
                if (!window.INIStdPay) return alert('INIStdPay Loading Error');
                const form = document.createElement('form');
                form.id = 'payFrom';
                form.method = 'POST';
                document.body.appendChild(form);

                const returnUrl =
                    `${window.location.origin}/resumes/payment-result` +
                    `?device=${device}` +
                    `&totPrice=${totalPay}` +
                    `&productCode=${productCode}` +
                    `&memberSeq=${mSeq}` +
                    `&memberId=${memberId}` +
                    `&receiptNum=${payloadReceipt?.receiptNum || ''}`;
                const closeUrl = `${window.location.origin}/payment-cancel`;

                if (isMobile) {
                    const inputs = [
                        { name: 'P_INI_PAYMENT', value: paymentMethod },
                        { name: 'P_MID', value: data.mid },
                        { name: 'P_OID', value: data.oid },
                        { name: 'P_AMT', value: totalPay },
                        { name: 'P_GOODS', value: `${goodName}` },
                        { name: 'P_UNAME', value: `${payloadReceipt?.nameKor}` },
                        { name: 'P_MOBILE', value: `${payloadReceipt?.mobileNo}` },
                        { name: 'P_EMAIL', value: `${payloadReceipt?.email}` },
                        { name: 'P_RESERVED', value: acceptMethodValue },
                        { name: 'P_NEXT_URL', value: returnUrl },
                        { name: 'P_NOTI_URL', value: 'https://api.dentaljob.co.kr/api/payment/vbank-callback' },
                        { name: 'P_CHARSET', value: 'utf8' },
                    ];
                    inputs.forEach(({ name, value }) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = name;
                        input.value = value;
                        form.appendChild(input);
                        form.acceptCharset = 'EUC-KR';
                        form.action = 'https://mobile.inicis.com/smart/payment/';
                    });
                    form.submit();
                } else {
                    const inputs = [
                        { name: 'version', value: '1.0' },
                        { name: 'gopaymethod', value: paymentMethod },
                        { name: 'mid', value: data.mid },
                        { name: 'oid', value: data.oid },
                        { name: 'price', value: totalPay },
                        { name: 'timestamp', value: data.timestamp },
                        { name: 'use_chkfake', value: 'Y' },
                        { name: 'signature', value: data.signature },
                        { name: 'verification', value: data.verification },
                        { name: 'mKey', value: data.mKey },
                        { name: 'currency', value: 'WON' },
                        { name: 'goodname', value: `${goodName}` },
                        { name: 'buyername', value: `${payloadReceipt?.nameKor}` },
                        { name: 'buyertel', value: `${payloadReceipt?.mobileNo}` },
                        { name: 'buyeremail', value: `${payloadReceipt?.email}` },
                        { name: 'acceptmethod', value: acceptMethodValue },
                        { name: 'returnUrl', value: returnUrl },
                        { name: 'closeUrl', value: closeUrl },
                    ];
                    inputs.forEach(({ name, value }) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = name;
                        input.value = value;
                        form.appendChild(input);
                    });
                    window.INIStdPay.pay('payFrom');
                }
            })
            .catch((error) => console.log(error));
    };

    return (
        <div className="rsm-ticket-purchase" role="dialog" aria-modal="true" aria-labelledby="ticketPurchaseTitle">
            <div className="rsm-ticket-purchase__container">
                <div className="rsm-ticket-purchase__header">
                    <h2 id="ticketPurchaseTitle">이력서 열람권 구매</h2>
                    <button
                        type="button"
                        className="rsm-ticket-purchase__close"
                        aria-label="닫기"
                        onClick={onClose}
                    />
                </div>
                <div className="rsm-ticket-purchase__body">
                    <div className="rsm-ticket-purchase__block">
                        <div className="byn__tit">이력서 열람권 상품 선택</div>
                        <div className="rsm-ticket-purchase__table-frame">
                            <table className="pym__table rsm-ticket-purchase__table" style={ticketPurchaseTableStyles.table}>
                                <colgroup>
                                    <col style={ticketPurchaseTableStyles.colGroup} />
                                    <col style={ticketPurchaseTableStyles.colCheck} />
                                    <col style={ticketPurchaseTableStyles.colName} />
                                    <col style={ticketPurchaseTableStyles.colPrice} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={ticketPurchaseTableStyles.headerCell}>구분</th>
                                        <th colSpan={2} style={ticketPurchaseTableStyles.headerCell}>
                                            상품명
                                        </th>
                                        <th style={ticketPurchaseTableStyles.headerCell}>가격</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ticketProducts.map((product, index) => {
                                        const isFirstInGroup =
                                            index === 0 || ticketProducts[index - 1].group !== product.group;
                                        const rowSpan = ticketProducts.filter((item) => item.group === product.group).length;

                                        return (
                                            <tr key={product.id}>
                                                {isFirstInGroup && (
                                                    <td
                                                        rowSpan={rowSpan}
                                                        className="rsm-ticket-purchase__group"
                                                        style={ticketPurchaseTableStyles.groupCell}
                                                    >
                                                        {product.group}
                                                    </td>
                                                )}
                                                <td
                                                    className="center rsm-ticket-purchase__check"
                                                    style={ticketPurchaseTableStyles.checkCell}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="rgst__checkbox"
                                                        checked={selectedProduct === product.id}
                                                        onChange={() => setSelectedProduct(product.id)}
                                                    />
                                                </td>
                                                <td style={ticketPurchaseTableStyles.cell}>{product.name}</td>
                                                <td className="center" style={ticketPurchaseTableStyles.priceCell}>
                                                    {product.price}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="rsm-ticket-purchase__notice">
                        <h3>안내사항</h3>
                        <p>※ 횟수 차감 상품의 경우, 구매 즉시 사용 가능합니다.</p>
                        <p>※ 횟수 차감 상품에 한하여, 미사용 시 7일 이내에 환불이 가능합니다.</p>
                        <p>※ 횟수 차감 상품으로 열람한 이력서가 24시간 이내에 삭제 또는 비공개 전환 되는 경우, 사용한 횟수는 다시 반환됩니다.</p>
                        <p>※ 기간 상품의 경우, 구매 즉시 사용되지 않으며 [마이페이지 - 열람권 관리/영수증] 페이지에서 별도로 사용 버튼을 클릭하여 사용해주셔야 합니다.</p>

                    </div>
                    <div className="byn__block rsm-ticket-purchase__block rsm-ticket-purchase__payment-block">
                        <div className="byn__tit">결제수단/증빙 선택</div>
                        <div className="rsm-ticket-purchase__payment-frame">
                            <table className="byn__table rsm-ticket-purchase__payment">
                                <tbody>
                                    <tr className="byn__wrap">
                                        <th>
                                            <span className="byn__req">*</span>결제수단
                                        </th>
                                        <td>
                                            <fieldset className="byn_radio">
                                                <div className="item-radioBox">
                                                    <input
                                                        type="radio"
                                                        name="ticket-payment-method"
                                                        id="cardMethod"
                                                        value="Card"
                                                        checked={paymentMethod === 'Card'}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                    />
                                                    <span />
                                                    <label htmlFor="cardMethod">
                                                        <span>신용카드</span>
                                                    </label>
                                                </div>
                                                <div className="item-radioBox">
                                                    <input
                                                        type="radio"
                                                        name="ticket-payment-method"
                                                        id="directBankMethod"
                                                        value="DirectBank"
                                                        checked={paymentMethod === 'DirectBank'}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                    />
                                                    <span />
                                                    <label htmlFor="directBankMethod">
                                                        <span>실시간계좌이체</span>
                                                    </label>
                                                </div>
                                                <div className="item-radioBox">
                                                    <input
                                                        type="radio"
                                                        name="ticket-payment-method"
                                                        id="VBankMethod"
                                                        value="VBank"
                                                        checked={paymentMethod === 'VBank'}
                                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                                    />
                                                    <span />
                                                    <label htmlFor="VBankMethod">
                                                        <span>무통장입금</span>
                                                    </label>
                                                </div>
                                            </fieldset>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>
                                            <span className="byn__req">*</span>결제자 정보
                                        </th>
                                        <td className="wrap-column">
                                            <div className="byn__itxt">
                                                <span>성명:</span>
                                                <input
                                                    type="text"
                                                    placeholder="이름을 입력하세요"
                                                    name="nameKor"
                                                    value={payloadReceipt?.nameKor || ''}
                                                    onChange={handleChangeReceipt}
                                                />
                                            </div>
                                            <div className="byn__itxt">
                                                <span>휴대폰:</span>
                                                <input
                                                    type="text"
                                                    placeholder="휴대폰 번호를 입력하세요"
                                                    name="mobileNo"
                                                    value={payloadReceipt?.mobileNo || ''}
                                                    onChange={handleChangeReceipt}
                                                />
                                            </div>
                                            <div className="byn__itxt">
                                                <span>이메일:</span>
                                                <input
                                                    type="text"
                                                    placeholder="이메일을 입력하세요"
                                                    name="email"
                                                    value={payloadReceipt?.email || ''}
                                                    onChange={handleChangeReceipt}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="byn__wrap">
                                        <th>결제 안내사항</th>
                                        <td className="td-wrap">
                                            <ul>
                                                <li>
                                                    열람권 구매 현황 및 영수증 출력은 [마이페이지 - 열람권 관리/영수증] 메뉴에서 확인하실 수 있습니다.
                                                </li>
                                                <li>
                                                    결제 신청시, 승인 진행에 다소 시간이 소요될 수 있으므로 ‘중지’ ,
                                                    ‘새로고침’을 누르지 마시고 결과 화면이 나타날 때까지 기다려 주십시오.
                                                </li>
                                                <li className="red noMaker">
                                                    <p>※</p>
                                                    <p>
                                                        [필독!] 무통장입금(가상계좌) 선택시&nbsp;하나은행(구,외환은행)으로
                                                        선택하신 경우는 인터넷뱅킹등으로 입금하실 때
                                                        입금은행을&nbsp;외환은행으로 선택하세요.
                                                    </p>
                                                </li>
                                                <li className="blue">
                                                    DVmall 덴탈잡이용권은 DVmall사이트에서 할인된 가격으로 구입 가능합니다.
                                                </li>
                                                <li className="blue">
                                                    구입하신 DVmall 덴탈잡이용권은 DVmall에 인증된 비타민아이디와 덴탈잡에
                                                    로그인한 아이디가 동일한 경우 사용 가능합니다.
                                                </li>
                                                <li className="blue">
                                                    DVmall 덴탈잡이용권에 대한 증빙서류는 구입하신 DVmall 사이트에서
                                                    확인하시기 바랍니다.
                                                </li>
                                            </ul>
                                        </td>
                                    </tr>
                                    {paymentMethod === 'Card' && (
                                        <tr className="byn__wrap">
                                            <th>신용카드 안내</th>
                                            <td>
                                                - 신용카드 매출전표는 부가가치세법 제32조의 2 제3항에 의하여 발행되며, 동법
                                                시행령 제57조 제2항에 의하여 세금계산서를 교부하지 않습니다.
                                            </td>
                                        </tr>
                                    )}
                                    {(paymentMethod === 'DirectBank' || paymentMethod === 'VBank') && (
                                        <>
                                            {paymentMethod === 'DirectBank' && (
                                                <tr className="byn__wrap">
                                                    <th>실시간계좌이체 안내</th>
                                                    <td className="td-wrap">
                                                        <ul>
                                                            <li>
                                                                결제금액이 입력하신 계좌에서 자동으로 이체되는 서비스로
                                                                계좌이체시 본인인증이 필요합니다.
                                                            </li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}

                                            {paymentMethod === 'VBank' && (
                                                <tr className="byn__wrap">
                                                    <th>무통장입금 안내</th>
                                                    <td className="td-wrap">
                                                        <ul>
                                                            <li>
                                                                무통장 입금 확인은 입금 후 최대 1시간 이내에 확인되며,
                                                                입금 확인시 열람권이 지급됩니다.
                                                            </li>
                                                            <li>
                                                                입금확인이 하루 이상(주말, 공휴일 제외) 지속될 경우
                                                                고객센터로 연락주시기 바랍니다.
                                                            </li>
                                                            <li>
                                                                무통장 주문 후 5일이 지날때까지 입금이 되지 않으면
                                                                주문은 자동으로 취소됩니다.
                                                            </li>
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}

                                            <tr>
                                                <th>
                                                    <span className="byn__req">*</span>현금영수증 발급
                                                </th>
                                                <td className="td-wrap">
                                                    <div className="wrap-top">
                                                        <div className="wrap-radioBox">
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="receiptType"
                                                                    value="company"
                                                                    checked={payloadReceipt.receiptType === 'company'}
                                                                    onChange={handleChangeReceipt}
                                                                />
                                                                <span className="title-value-radio">
                                                                    지출증빙용(사업자, 세금계산서대용)
                                                                </span>
                                                            </label>
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="receiptType"
                                                                    value="person"
                                                                    checked={payloadReceipt.receiptType === 'person'}
                                                                    onChange={handleChangeReceipt}
                                                                />
                                                                <span className="title-value-radio">
                                                                    소득공제용(개인)
                                                                </span>
                                                            </label>
                                                        </div>
                                                        <label className="title-value-text">
                                                            <span>*신청번호(사업자번호, 핸드폰번호) : </span>
                                                            <input
                                                                type="text"
                                                                name="receiptNum"
                                                                placeholder="-없이 입력하세요"
                                                                value={payloadReceipt?.receiptNum || ''}
                                                                onChange={handleChangeReceipt}
                                                            />
                                                        </label>
                                                    </div>
                                                    <div className="wrap-bottom">
                                                        <div className="title">※현금영수증 발급 안내</div>
                                                        <ul>
                                                            <li>
                                                                현금영수증(지출증빙)은 법인세법 116조 2항 및
                                                                부가가치세법 32조 2의 3항에 의해 세금계산서와 동일한
                                                                자격 및 효력이 있습니다
                                                            </li>
                                                            <li>
                                                                세금계산서를 원할 경우 현금영수증(지출증빙)으로
                                                                신청하시면 세금계산서 대용으로 세액공제를 받을 수
                                                                있습니다.
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="byn__btn rsm-ticket-purchase__submit"
                        onClick={handleSubmit}
                    >
                        결제하기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TicketPurchase;
